import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown, Sun, Moon } from "lucide-react";
import { openCalendly } from "@/lib/calendly";
import { useLang } from "@/lib/i18n";
import { useT } from "@/lib/useT";
import { useTheme } from "@/lib/theme";
import { prefetchRoute } from "@/lib/prefetchRoute";

const Navigation = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [projectsOpen, setProjectsOpen] = useState(false);
  const projectsRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { lang, toggle } = useLang();
  const { theme, toggle: toggleTheme } = useTheme();
  const { t } = useT();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Close mobile menu on route change.
  // Guard prevents an unconditional setState-in-effect (React 19 lint rule)
  // and avoids spurious re-renders when the menu is already closed.
  useEffect(() => {
    setMobileOpen((v) => (v ? false : v));
    setProjectsOpen((v) => (v ? false : v));
  }, [location.pathname]);

  // Close projects dropdown on outside click
  useEffect(() => {
    if (!projectsOpen) return;
    const onClick = (e) => {
      if (projectsRef.current && !projectsRef.current.contains(e.target)) setProjectsOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [projectsOpen]);

  const projectsList = [
    { name: "Off Market Karlshorst", sub: "Berlin-Karlshorst · Off-market opportunity", href: "/projects" },
    { name: "Amselweg 14B", sub: "Potsdam-Marquardt · Last unit · Q2 2026", href: "/projects/amselweg-14b" },
    { name: "Zechliner Straße 9a", sub: "Berlin-Gesundbrunnen · 1908 Altbau · 5.45 %", href: "/projects/zechliner-strasse-9a" },
  ];

  const navLinks = [
    { name: t("nav.services"), href: "/services" },
    { name: t("nav.objekte"), href: "/objekte" },
    { name: t("nav.projects"), href: "/projects", isProjects: true },
    { name: t("nav.financing"), href: "/financing" },
    { name: t("nav.process"), href: "/process" },
    { name: t("nav.results"), href: "/results" },
    { name: t("nav.team"), href: "/team" },
  ];

  const isActive = (link) => {
    if (link.isProjects) return location.pathname.startsWith("/projects");
    return location.pathname === link.href;
  };

  const handleNavClick = (e, link) => {
    e.preventDefault();
    if (link.isProjects) {
      setProjectsOpen((v) => !v);
      return;
    }
    navigate(link.href);
    setMobileOpen(false);
  };

  return (
    <motion.header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-700 ${
        isScrolled || mobileOpen
          ? "bg-[#0a0a0a]/90 backdrop-blur-2xl border-b border-white/[0.04]"
          : "bg-transparent"
      }`}
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.6 }}
    >
      <div className="max-w-[1800px] mx-auto px-6 sm:px-8 md:px-16 lg:px-24">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <a
            href="/"
            onClick={(e) => { e.preventDefault(); navigate("/"); setMobileOpen(false); }}
            className="font-heading text-lg md:text-xl tracking-wider text-white shrink-0"
            data-testid="nav-logo"
          >
            IRE <span className="text-ire-accent">BERLIN</span>
          </a>

          {/* Desktop nav */}
          <nav className="hidden md:flex items-center gap-1 sm:gap-2 md:gap-3 lg:gap-8">
            {navLinks.map((link) => {
              const active = isActive(link);
              if (link.isProjects) {
                return (
                  <div key={link.name} ref={projectsRef} className="relative">
                    <button
                      type="button"
                      onClick={(e) => handleNavClick(e, link)}
                      className="relative group px-2 sm:px-3 py-2 flex items-center gap-1.5"
                      data-testid={`nav-link-${link.name.toLowerCase()}`}
                      aria-expanded={projectsOpen}
                    >
                      <span className={`font-body text-[11px] sm:text-xs md:text-sm tracking-wide transition-colors duration-300 ${active ? "text-ire-accent" : "text-white/50 group-hover:text-white"}`}>{link.name}</span>
                      <ChevronDown className={`w-3 h-3 transition-transform duration-300 ${active ? "text-ire-accent" : "text-white/40"} ${projectsOpen ? "rotate-180" : ""}`} />
                      <span className={`absolute bottom-0 left-1/2 -translate-x-1/2 h-[1px] bg-ire-accent transition-all duration-500 ${active ? "w-full" : "w-0 group-hover:w-3/4"}`} />
                      {active && (<span className="absolute bottom-0 left-1/2 -translate-x-1/2 w-8 h-[1px] bg-ire-accent blur-[4px]" />)}
                    </button>
                    <AnimatePresence>
                      {projectsOpen && (
                        <motion.div
                          initial={{ opacity: 0, y: -8 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -8 }}
                          transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
                          className="absolute top-full left-1/2 -translate-x-1/2 mt-3 w-[340px] bg-[#0a0a0a]/98 backdrop-blur-2xl border border-ire-accent/15 shadow-[0_24px_60px_-20px_rgba(0,0,0,0.8),0_0_60px_-30px_rgba(212,175,106,0.4)]"
                          data-testid="nav-projects-dropdown"
                        >
                          <div className="px-5 py-4 border-b border-white/[0.05] flex items-center justify-between gap-3">
                            <div className="font-body text-[10px] tracking-[0.3em] text-ire-accent uppercase">Flagship Projects</div>
                            <a
                              href="/objekte"
                              onClick={(e) => { e.preventDefault(); navigate("/objekte"); setProjectsOpen(false); }}
                              onMouseEnter={() => prefetchRoute("/objekte")}
                              data-testid="nav-projects-all-objekte"
                              className="font-body text-[10px] tracking-[0.25em] uppercase text-white/50 hover:text-ire-accent transition-colors"
                            >
                              {lang === "de" ? "Alle Objekte →" : "All exposés →"}
                            </a>
                          </div>
                          <div className="py-2">
                            {projectsList.map((p) => {
                              const isCurrent = location.pathname === p.href;
                              return (
                                <a
                                  key={p.href}
                                  href={p.href}
                                  onClick={(e) => { e.preventDefault(); navigate(p.href); setProjectsOpen(false); }}
                                  onMouseEnter={() => prefetchRoute(p.href)}
                                  onTouchStart={() => prefetchRoute(p.href)}
                                  onFocus={() => prefetchRoute(p.href)}
                                  data-testid={`nav-projects-item-${p.name.toLowerCase().replace(/\s+/g, "-")}`}
                                  className={`block px-5 py-3.5 group transition-colors duration-300 ${isCurrent ? "bg-ire-accent/[0.06]" : "hover:bg-white/[0.03]"}`}
                                >
                                  <div className="flex items-baseline justify-between gap-3">
                                    <span className={`font-heading text-sm tracking-wide ${isCurrent ? "text-ire-accent" : "text-white group-hover:text-ire-accent"} transition-colors duration-300`}>{p.name}</span>
                                    {isCurrent && (<span className="font-body text-[9px] tracking-[0.2em] uppercase text-ire-accent/70">Current</span>)}
                                  </div>
                                  <div className="font-body text-[11px] text-white/45 mt-1">{p.sub}</div>
                                </a>
                              );
                            })}
                          </div>
                          <div className="px-5 py-3 border-t border-white/[0.05] font-body text-[10px] text-white/40 leading-relaxed">
                            More flagship projects added through 2026.
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                );
              }
              return (
                <a
                  key={link.name}
                  href={link.href}
                  onClick={(e) => handleNavClick(e, link)}
                  onMouseEnter={() => prefetchRoute(link.href)}
                  onTouchStart={() => prefetchRoute(link.href)}
                  onFocus={() => prefetchRoute(link.href)}
                  className="relative group px-2 sm:px-3 py-2"
                  data-testid={`nav-link-${link.name.toLowerCase()}`}
                >
                  <span
                    className={`font-body text-[11px] sm:text-xs md:text-sm tracking-wide transition-colors duration-300 ${
                      active ? "text-ire-accent" : "text-white/50 group-hover:text-white"
                    }`}
                  >
                    {link.name}
                  </span>
                  <span
                    className={`absolute bottom-0 left-1/2 -translate-x-1/2 h-[1px] bg-ire-accent transition-all duration-500 ${
                      active ? "w-full" : "w-0 group-hover:w-3/4"
                    }`}
                  />
                  {active && (
                    <span className="absolute bottom-0 left-1/2 -translate-x-1/2 w-8 h-[1px] bg-ire-accent blur-[4px]" />
                  )}
                </a>
              );
            })}
          </nav>

          <div className="flex items-center gap-2 sm:gap-3">
            {/* Theme toggle */}
            <button
              onClick={toggleTheme}
              className="w-8 h-8 sm:w-9 sm:h-9 border border-white/[0.08] flex items-center justify-center hover:border-ire-accent/30 transition-all duration-300"
              data-testid="theme-toggle"
              title={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
              aria-label="Toggle theme"
            >
              {theme === "dark"
                ? <Sun className="w-3.5 h-3.5 text-white/50 hover:text-ire-accent transition-colors" />
                : <Moon className="w-3.5 h-3.5 text-white/50 hover:text-ire-accent transition-colors" />}
            </button>

            {/* Language toggle */}
            <button
              onClick={toggle}
              className="w-8 h-8 sm:w-9 sm:h-9 border border-white/[0.08] flex items-center justify-center hover:border-ire-accent/30 transition-all duration-300"
              data-testid="lang-toggle"
              title={lang === "en" ? "Auf Deutsch wechseln" : "Switch to English"}
            >
              <span className="font-body text-[10px] sm:text-xs font-medium text-white/50 hover:text-white transition-colors">
                {lang === "en" ? "DE" : "EN"}
              </span>
            </button>

            {/* CTA — hidden on smallest mobile */}
            <button
              onClick={() => { navigate("/apply"); setMobileOpen(false); }}
              className="hidden sm:inline-flex relative shrink-0 font-body text-[10px] sm:text-xs tracking-[0.15em] uppercase text-ire-accent border border-ire-accent/30 px-4 sm:px-6 py-2.5 sm:py-3 hover:bg-ire-accent hover:text-black transition-all duration-500 overflow-hidden group"
              data-testid="nav-cta"
            >
              <span className="relative z-10">{t("nav.apply")}</span>
            </button>

            {/* Mobile hamburger */}
            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              className="md:hidden w-10 h-10 flex items-center justify-center text-white/60"
              data-testid="nav-mobile-toggle"
            >
              {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="md:hidden border-t border-white/[0.04] bg-[#0a0a0a]/95 backdrop-blur-2xl overflow-hidden"
          >
            <div className="px-6 py-4 space-y-1">
              {navLinks.map((link) => {
                const active = isActive(link);

                // Projects = inline accordion on mobile so clients can pick a
                // dealroom directly. Previously this triggered the desktop
                // dropdown state which was hidden via `md:flex hidden` → nothing
                // appeared and clients reported "clicking Projects does nothing".
                if (link.isProjects) {
                  return (
                    <div key={link.name}>
                      <button
                        type="button"
                        onClick={() => setProjectsOpen((v) => !v)}
                        aria-expanded={projectsOpen}
                        className={`w-full flex items-center justify-between px-4 py-3 font-body text-sm tracking-wide transition-colors duration-300 ${
                          active ? "text-ire-accent bg-ire-accent/5" : "text-white/50"
                        }`}
                        data-testid="nav-mobile-projects"
                      >
                        <span>{link.name}</span>
                        <ChevronDown
                          className={`w-4 h-4 transition-transform duration-300 ${projectsOpen ? "rotate-180" : ""}`}
                        />
                      </button>
                      <AnimatePresence>
                        {projectsOpen && (
                          <motion.div
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: "auto" }}
                            exit={{ opacity: 0, height: 0 }}
                            transition={{ duration: 0.25 }}
                            className="overflow-hidden border-l border-ire-accent/20 ml-4 mb-1"
                            data-testid="nav-mobile-projects-submenu"
                          >
                            <a
                              href="/objekte"
                              onClick={(e) => {
                                e.preventDefault();
                                setProjectsOpen(false);
                                setMobileOpen(false);
                                navigate("/objekte");
                              }}
                              className="block px-4 py-3 hover:bg-white/[0.03] transition-colors"
                              data-testid="nav-mobile-projects-all-objekte"
                            >
                              <div className="font-body text-sm text-ire-accent tracking-wide">{lang === "de" ? "Alle Objekte im Angebot →" : "All live exposés →"}</div>
                              <div className="font-body text-[10px] text-white/40 mt-0.5 tracking-wide">{lang === "de" ? "Übersicht aller Exposés" : "Overview of every exposé"}</div>
                            </a>
                            {projectsList.map((p) => (
                              <a
                                key={p.href}
                                href={p.href}
                                onClick={(e) => {
                                  e.preventDefault();
                                  navigate(p.href);
                                  setProjectsOpen(false);
                                  setMobileOpen(false);
                                }}
                                className="block px-4 py-3 hover:bg-white/[0.03] transition-colors"
                                data-testid={`nav-mobile-projects-item-${p.name.toLowerCase().replace(/\s+/g, "-")}`}
                              >
                                <div className="font-body text-sm text-white/90">{p.name}</div>
                                <div className="font-body text-[10px] text-white/40 mt-0.5 tracking-wide">{p.sub}</div>
                              </a>
                            ))}
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  );
                }

                return (
                  <a
                    key={link.name}
                    href={link.href}
                    onClick={(e) => handleNavClick(e, link)}
                    className={`block px-4 py-3 font-body text-sm tracking-wide transition-colors duration-300 ${
                      active ? "text-ire-accent bg-ire-accent/5" : "text-white/50"
                    }`}
                    data-testid={`nav-mobile-${link.name.toLowerCase()}`}
                  >
                    {link.name}
                  </a>
                );
              })}
              <a
                href="#consultation"
                onClick={(e) => {
                  if (location.pathname !== "/") {
                    e.preventDefault();
                    navigate("/#consultation");
                  }
                  setMobileOpen(false);
                }}
                className="block px-4 py-3 font-body text-sm tracking-wide text-ire-accent sm:hidden"
              >
                Apply
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};

export default Navigation;
